import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import {
  Building2,
  ChevronRight,
  FileText,
  Info,
  Link2,
  Lock,
  Mail,
  MessageSquare,
  Phone,
  ShieldAlert,
  ShieldCheck,
} from "lucide-react";
import { useContactTab } from "@/hooks/useContacts";
import { useAuthStore } from "@/store/useAuthStore";
import type {
  ContactAccountItem,
  ContactActivityItem,
  ContactDetailEnvelope,
  ContactMergeCandidate,
  ContactMethodItem,
  ContactSignedContractItem,
} from "@/api/contacts";

/**
 * Phase 3.X-1B sub_2 — lazy tab panels for the Contact DETAIL.
 * Each renders as a nested route child of /contacts/:contactId, fetches its own
 * endpoint on mount via useContactTab, and reads the envelope from <Outlet context>.
 */

function useContactId(): string {
  const { contactId } = useParams<{ contactId: string }>();
  return contactId ?? "";
}

function TabHint({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-2 flex items-center gap-1.5 text-xs text-muted-foreground">
      <Info className="h-3.5 w-3.5" /> {children}
    </p>
  );
}
function Empty({ text }: { text: string }) {
  return <div className="rounded-lg border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">{text}</div>;
}
function Restricted({ text }: { text: string }) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed px-4 py-8 text-center">
      <Lock className="h-5 w-5 text-muted-foreground/50" />
      <span className="text-sm text-muted-foreground">{text}</span>
    </div>
  );
}
function TabError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed px-4 py-8 text-center">
      <ShieldAlert className="h-5 w-5 text-destructive" />
      <span className="text-sm text-muted-foreground">Не вдалося завантажити</span>
      <button onClick={onRetry} className="text-sm font-medium text-primary">Повторити</button>
    </div>
  );
}
function TabLoading() {
  return (
    <div className="space-y-1.5">
      {[0, 1, 2].map((i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-muted" />)}
    </div>
  );
}

const CHANNEL_ICON = { phone: Phone, email: Mail, messenger: MessageSquare, other: Info } as const;

// ── Tab: Огляд (identity overview — envelope-only, no extra fetch) ────────────
export function ContactOverviewTab() {
  const env = useOutletContext<ContactDetailEnvelope>();
  const idn = env.identity;
  const ms = env.methodsSummary;
  // BUNDLE 1 dual-path: ON adds holding→account «Прив'язки» + «Нотатки» sections.
  const customView = useAuthStore((s) => s.featureFlags?.customContactView ?? false);
  const facts: Array<{ label: string; value: string }> = [
    { label: "Роль", value: idn.roleLabel || idn.position || "—" },
    { label: "Клієнт", value: env.context.clientDisplayName || "—" },
    { label: "Видимих каналів", value: String(ms.totalVisibleCount) },
    { label: "Підписаних договорів", value: String(env.signedContracts.count) },
  ];
  return (
    <div className="p-6 md:p-8 space-y-4">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {facts.map((f) => (
          <div key={f.label} className="rounded-lg border bg-card px-3 py-2">
            <div className="text-[11px] text-muted-foreground">{f.label}</div>
            <div className="mt-0.5 truncate text-sm font-semibold">{f.value}</div>
          </div>
        ))}
      </div>

      {customView && env.context.clientDisplayName && (
        <div className="rounded-lg border bg-card p-4">
          <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
            <Link2 className="h-3 w-3" /> Прив'язки
          </div>
          <div className="flex flex-wrap items-center gap-1.5 text-sm">
            <Building2 className="h-3.5 w-3.5 text-primary" />
            <span>{env.context.clientDisplayName}</span>
            {env.context.accountDisplayName && env.context.accountDisplayName !== env.context.clientDisplayName && (
              <>
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                <span>{env.context.accountDisplayName}</span>
              </>
            )}
            {idn.isPrimary && <span className="ml-1 text-xs text-primary">· основний</span>}
          </div>
        </div>
      )}

      <div className="rounded-lg border bg-card p-4">
        <div className="mb-2 text-sm font-semibold">Основні канали</div>
        {ms.primaryVisible.length === 0 ? (
          <Empty text="Видимих каналів немає" />
        ) : (
          <div className="space-y-1.5">
            {ms.primaryVisible.map((m) => <MethodRow key={m.id} m={m} />)}
          </div>
        )}
      </div>

      {customView && idn.notes.trim() !== "" && (
        <div className="rounded-lg border bg-card p-4">
          <div className="mb-1 text-[11px] font-medium text-muted-foreground">Нотатки (CRM)</div>
          <div className="whitespace-pre-wrap text-sm text-foreground/80">{idn.notes}</div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 text-xs">
        {idn.doNotCall && <Flag text="Не телефонувати" />}
        {idn.doNotEmail && <Flag text="Не писати e-mail" />}
        {idn.doNotMessage && <Flag text="Не писати в месенджер" />}
        <span className="rounded-full bg-muted px-2 py-1 text-muted-foreground">
          Згода на маркетинг: {idn.marketingConsent}
        </span>
      </div>
    </div>
  );
}

function Flag({ text }: { text: string }) {
  return <span className="rounded-full bg-warning px-2 py-1 font-medium text-warning-foreground">{text}</span>;
}

function MethodRow({ m }: { m: ContactMethodItem }) {
  const Icon = CHANNEL_ICON[m.channel] ?? Info;
  return (
    <div className="flex items-center gap-2 text-sm">
      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="font-medium">{m.value}</span>
      {m.isPrimary && <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">основний</span>}
      {m.label && <span className="text-xs text-muted-foreground">{m.label}</span>}
    </div>
  );
}

// ── Tab: Канали зв'язку (methods) ─────────────────────────────────────────────
export function ContactMethodsTab() {
  const id = useContactId();
  const q = useContactTab<ContactMethodItem>(id, "methods", true);
  return (
    <div className="p-6 md:p-8">
      <TabHint>Показано лише канали, доступні вам. Приховані канали не відображаються.</TabHint>
      {q.isPending ? <TabLoading /> : q.isError ? <TabError onRetry={() => q.refetch()} /> : q.data?.restricted ? (
        <Restricted text="Перегляд каналів недоступний" />
      ) : (q.data?.items.length ?? 0) === 0 ? (
        <Empty text="Каналів зв'язку немає" />
      ) : (
        <div className="space-y-1.5">
          {q.data!.items.map((m) => (
            <div key={m.id} className="rounded-lg border bg-card px-3 py-2.5"><MethodRow m={m} /></div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Tab: Контекст (accounts/holdings) ─────────────────────────────────────────
export function ContactAccountsTab() {
  const id = useContactId();
  const navigate = useNavigate();
  const q = useContactTab<ContactAccountItem>(id, "accounts", true);
  return (
    <div className="p-6 md:p-8">
      <TabHint>Клієнти, юрособи та точки, до яких призначено контакт.</TabHint>
      {q.isPending ? <TabLoading /> : q.isError ? <TabError onRetry={() => q.refetch()} /> : q.data?.restricted ? (
        <Restricted text="Перегляд контексту недоступний" />
      ) : (q.data?.items.length ?? 0) === 0 ? (
        <Empty text="Контакт не призначено" />
      ) : (
        <div className="space-y-1.5">
          {q.data!.items.map((a) => {
            const clickable = !!a.clientId;
            return (
              <button
                key={a.id}
                disabled={!clickable}
                onClick={() => a.clientId && navigate(`/clients/${a.clientId}`)}
                className="flex w-full items-center gap-3 rounded-lg border bg-card px-3 py-2.5 text-left enabled:hover:border-primary/40 disabled:cursor-default"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{a.targetName}</span>
                    {a.isPrimary && <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">основний</span>}
                  </div>
                  <span className="text-xs text-muted-foreground">{a.role || a.targetType}</span>
                </div>
                {clickable && <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/50" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Tab: Активності (interactions) ────────────────────────────────────────────
export function ContactActivitiesTab() {
  const id = useContactId();
  const q = useContactTab<ContactActivityItem>(id, "activities", true);
  return (
    <div className="p-6 md:p-8">
      <TabHint>Взаємодії з цим контактом.</TabHint>
      {q.isPending ? <TabLoading /> : q.isError ? <TabError onRetry={() => q.refetch()} /> : q.data?.restricted ? (
        <Restricted text="Перегляд активностей недоступний" />
      ) : (q.data?.items.length ?? 0) === 0 ? (
        <Empty text="Активностей ще немає" />
      ) : (
        <div className="space-y-1.5">
          {q.data!.items.map((a) => (
            <div key={a.id} className="rounded-lg border bg-card px-3 py-2.5">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium">{a.typeLabel}</span>
                {a.occurredAt && <span className="shrink-0 text-xs text-muted-foreground">{a.occurredAt.slice(0, 16).replace("T", " ")}</span>}
              </div>
              {a.summary && <div className="mt-0.5 text-sm text-muted-foreground">{a.summary}</div>}
              <div className="mt-0.5 flex flex-wrap gap-2 text-xs text-muted-foreground">
                {a.accountName && <span>{a.accountName}</span>}
                {a.outcome && <><span>·</span><span>{a.outcome}</span></>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Tab: Підписані договори (signed contracts → F4) ───────────────────────────
export function ContactContractsTab() {
  const id = useContactId();
  const navigate = useNavigate();
  const q = useContactTab<ContactSignedContractItem>(id, "contracts", true);
  return (
    <div className="p-6 md:p-8">
      <TabHint>Договори, підписані цим контактом. Відкриваються у картці договору.</TabHint>
      {q.isPending ? <TabLoading /> : q.isError ? <TabError onRetry={() => q.refetch()} /> : q.data?.restricted ? (
        <Restricted text="Перегляд договорів недоступний" />
      ) : (q.data?.items.length ?? 0) === 0 ? (
        <Empty text="Підписаних договорів немає" />
      ) : (
        <div className="space-y-1.5">
          {q.data!.items.map((c) => (
            <button
              key={c.id}
              onClick={() => navigate(`/contracts/${c.id}`)}
              className="flex w-full items-center gap-3 rounded-lg border bg-card px-3 py-2.5 text-left hover:border-primary/40"
            >
              <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <span className="text-sm font-medium">{c.contractNumber || "Без номера"}</span>
                <div className="text-xs text-muted-foreground">
                  {c.accountName}{c.expirationDate ? ` · до ${c.expirationDate}` : ""}
                </div>
              </div>
              {c.stateLabel && <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">{c.stateLabel}</span>}
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/50" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Tab: Дублікати / статус злиття (merge — VIEW-ONLY) ────────────────────────
export function ContactMergeTab() {
  const id = useContactId();
  const q = useContactTab<ContactMergeCandidate>(id, "merge-status", true);
  return (
    <div className="p-6 md:p-8">
      <TabHint>Перегляд статусу дублікатів. Злиття виконується в адмін-процесі (наразі недоступно тут).</TabHint>
      {q.isPending ? <TabLoading /> : q.isError ? <TabError onRetry={() => q.refetch()} /> : q.data?.restricted ? (
        <Restricted text="Перегляд недоступний" />
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-card px-3 py-2.5 text-sm">
            <ShieldCheck className="h-4 w-4 text-muted-foreground" />
            {q.data?.isMergedInto ? (
              <span>Об'єднано у контакт <b>{q.data.isMergedInto.name}</b></span>
            ) : q.data?.isCanonical ? (
              <span>Канонічний контакт для <b>{q.data.canonicalOfCount}</b> дубліката(ів)</span>
            ) : (
              <span>Контакт не об'єднано</span>
            )}
          </div>

          {(q.data?.items.length ?? 0) === 0 ? (
            <Empty text="Можливих дублікатів немає" />
          ) : (
            <div className="space-y-1.5">
              {q.data!.items.map((d) => (
                <div key={d.id} className="flex items-center gap-3 rounded-lg border bg-card px-3 py-2.5">
                  <div className="min-w-0 flex-1">
                    <span className="text-sm font-medium">{d.otherContactName}</span>
                    <div className="text-xs text-muted-foreground">
                      Статус: {d.status}{d.confidence ? ` · впевненість: ${d.confidence}` : ""}
                    </div>
                  </div>
                  {d.score != null && <span className="text-xs text-muted-foreground">{d.score.toFixed(2)}</span>}
                </div>
              ))}
            </div>
          )}
          {/* canMergeContact is hard-false for pilot — no merge action surfaced, never bounces to crmold (TZ §6/E4). */}
        </div>
      )}
    </div>
  );
}
